<template>
  <div
    v-if="isLoading"
    aria-live="polite"
  >
    <DbFunnelLoader>
      <template v-slot:title>
        {{ $t('We are looking for the best option for you') }}
      </template>
      <template v-slot:message>
        {{ $t('Thank you for your patience') }}
      </template>
    </DbFunnelLoader>
  </div>

  <!-- Hotelsearch container -->
  <!-- Mandatory to apply specific grid layout for list & map on desktop -->
  <div
    v-show="!isLoading"
    role="main"
    class="db-funnel-container db-hotelsearch-container"
    :class="{ 'db-map-layout': selectedLayout === 'map' && count }"
    :style="{ '--db-height-header': headerHeight ? `${headerHeight}px` : '0px' }"
  >
    <!-- Page header -->
    <div class="db-funnel-header">
      <!-- Page title -->
      <div class="db-funnel-title db-title-3">
        <h2 aria-live="polite">
          <slot name="title" />
        </h2>
      </div>

      <DbSwitch
        v-if="hasTotalPrice"
        v-model="seeTotalPrice"
        v-on:update:modelValue="(value: any) => eventData('view_results_by', {
        event_source: 'gaEventDelphiDirect',
        sort_by: value ? 'daily_price' : 'person_price',
      })"
        class="db-total-price-switch db-reverse"
        :inputAttrs="{
          name: 'total-price-switch',
        }"
      >
        <span class="db-bold db-uppercase">{{ $t('See total price') }}</span>
      </DbSwitch>

      <!-- Filters button -->
      <button
        v-if="filters.length"
        type="button"
        class="db-button-ghost-supporting db-button-filters db-item-start"
        v-on:click="modals.filters.isOpen = !modals.filters.isOpen"
      >
        <span>
          {{ $t('Filters') }}
          {{ getFilterTags.length > 0 ? `(${getFilterTags.length})` : null }}
        </span>
        <IconDbSettings
          width="20"
          height="20"
        />
      </button>

      <!-- Sorting select -->
      <DbSorting
        v-if="showSort && selectedSorting && sortingOptions.length"
        v-model="selectedSorting"
        :options="sortingOptions"
      />

      <!-- Layout controls -->
      <fieldset
        v-if="hasSeveralLayouts"
        class="db-layout-controls"
      >
        <legend class="db-visually-hidden">
          {{ $t('Choose layout') }}
        </legend>
        <input
          id="db-radio-layout-list"
          v-model="selectedLayout"
          class="db-visually-hidden db-layout-control"
          type="radio"
          name="layout"
          value="list"
        />
        <label
          for="db-radio-layout-list"
          class="db-button-square db-button-layout"
          :class="selectedLayout === 'list' ? 'db-button-supporting' : 'db-button-ghost-supporting'"
        >
          <IconDbGrid
            v-if="isGridLayoutDefined === true"
            width="20"
            height="20"
          />
          <IconDbList
            v-else
            width="20"
            height="20"
          />
          <span class="db-visually-hidden">
            {{ $t('List layout') }}
          </span>
        </label>
        <input
          id="db-radio-layout-map"
          v-model="selectedLayout"
          class="db-visually-hidden db-layout-control"
          type="radio"
          name="layout"
          value="map"
        />
        <label
          for="db-radio-layout-map"
          class="db-button-square db-button-layout"
          :class="selectedLayout === 'map' ? 'db-button-supporting' : 'db-button-ghost-supporting'"
        >
          <IconDbPinMap
            width="20"
            height="20"
          />
          <span class="db-visually-hidden">
            {{ $t('Map layout') }}
          </span>
        </label>
      </fieldset>
      <!-- !Layout controls -->
    </div>
    <!-- !Page header -->

    <!-- Page content (list/map) -->
    <!-- List of filters and results -->
    <div
      id="db-results"
      class="db-funnel-results db-hotelsearch-results"
      tabindex="0"
      :aria-label="$t('Results')"
    >
      <DbFunnelFilterTags
        v-if="filters"
        :tags="getFilterTags"
        v-on:delete-option="(tag) => unselectOption(tag.key, tag.value)"
      />

      <!-- No results message -->
      <DbFunnelError v-if="!count">
        <template v-slot:title>
          <slot name="no-results-image" />
          <slot name="no-results-title">
            {{ $t('Unfortunately, we have not found any results for your search query.') }}
          </slot>
        </template>
        <template v-slot:message>
          <slot name="no-results-message">{{
            $t('Try removing or using less search filters.')
          }}</slot>
        </template>
      </DbFunnelError>

      <div v-show="count && !isLoading">
        <slot
          name="results"
          :seeTotalPrice="seeTotalPrice"
        />
      </div>
    </div>
    <!-- !List of filters and results -->

    <!-- Map -->
    <div
      v-if="currentLayout === 'map' && count"
      class="db-hotelsearch-map"
    >
      <slot name="map"> {{ $t('Map would go here') }}</slot>
    </div>
    <!-- !Map -->

    <DbModal
      v-if="modals.filters.isOpen"
      class="db-modal-side db-modal-filters"
      v-on:close-modal="closeFiltersForm"
    >
      <template v-slot:header>
        <IconDbSettings
          width="22"
          height="22"
        />
        <span class="db-title-1 db-tw-text-box-titles">
          {{ $t('Filters') }}
        </span>
      </template>

      <DbFunnelFiltersForm
        v-model="selectedData"
        class="db-modal-form"
        :filters="filters"
        v-on:done="closeFiltersForm"
      />
    </DbModal>
    <!-- !Filters modal -->
  </div>
</template>

<script setup lang="ts">
import { computed, reactive, ref } from 'vue'
import type {
  FilterGroup,
  Filters,
  Layouts,
  SelectedData,
  Option,
  SearchSort,
} from './SearchWrapperTypes'
import { useHeaderHeight } from '@/composables/useHeaderHeight'
import type { Tag } from './DbFunnelFilterTags.vue'
import { useI18n } from 'vue-i18n'
import { usePackagesStore } from '@/stores/packages'
import { useGtm } from '@/composables/features/useGtm'
const { eventData } = useGtm()

const props = withDefaults(
  defineProps<{
    /**
     * If the search is in loading state
     *
     * *Tech note:*
     * Even if the search is loading, we use `v-show`
     * to hide the results, to avoid re-building the
     * full list of results
     * */
    isLoading: boolean
    /**
     * Number of results, used in the title and to
     * display the error message if the count is 0
     */
    count: number
    /** If there is a map, this works with `currentLayout` props */
    hasMap?: boolean
    /** Current layout, this works with `hasMap` props */
    currentLayout?: Layouts
    /**
     * If the "See Total Price" is visible.
     *
     * Information is available in slots
     */
    hasTotalPrice: boolean
    /**
     * Current sort applied.
     * If `currentSort` is not provided (undefined)
     * we will not show the sort dropdown
     *
     * For now the list of sort options is *hardcoded*
     * because it is the same for all searches
     */
    currentSort?: SearchSort
    showSort?: boolean
    filters?: Filters
    filtersData?: SelectedData
    isGridLayoutDefined?: boolean
  }>(),
  {
    currentLayout: undefined,
    filters: () => [],
    filtersData: () => ({}),
    hasMap: false,
    currentSort: undefined,
  },
)

const { t } = useI18n()

const emit = defineEmits<{
  (e: 'update:filters-data', newFilters: SelectedData): void
  (e: 'update:current-sort', newSort?: SearchSort): void
  (e: 'update:current-layout', newLayout?: Layouts): void
}>()

/** Sorting */
const sortingOptions = [
  { label: t('Recommended'), value: 'recommended' },
  { label: t('Price (high to low)'), value: 'price-desc' },
  { label: t('Price (low to high)'), value: 'price-asc' },
]

const selectedSorting = computed({
  get() {
    return props.currentSort
  },
  set(newSort) {
    if (newSort == 'price-desc')
      eventData('sort_results_by', {
        event_source: 'gaEventDelphiDirect',
        sort_by: 'price_high_to_low',
      })
    if (newSort == 'price-asc')
      eventData('sort_results_by', {
        event_source: 'gaEventDelphiDirect',
        sort_by: 'price_low_to_high',
      })
    if (newSort == 'recommended')
      eventData('sort_results_by', { event_source: 'gaEventDelphiDirect', sort_by: 'recommended' })
    emit('update:current-sort', newSort)
  },
})

const hasSeveralLayouts = computed(() => {
  return props.hasMap
})

const selectedLayout = computed({
  get() {
    return props.currentLayout
  },
  set(newLayout) {
    emit('update:current-layout', newLayout)
  },
})
const packagesStore = usePackagesStore()
const seeTotalPrice = ref(packagesStore.defaultSeeTotalPrice() == 'total')

/** Computed to handle v-model directive */
const selectedData = computed({
  get() {
    return props.filtersData
  },
  set(newData) {
    emit('update:filters-data', newData)
  },
})

/**
 * Aggregate filter list and filter data
 * to show the list of tags
 */
const getFilterTags = computed(() => {
  return Object.entries(selectedData.value).reduce((acc, [filterKey, options]) => {
    return acc.concat(
      options.map((valueId) => {
        const label = props.filters
          .find((f) => f.key === filterKey)
          ?.options.find((o) => o.value === valueId)?.label

        return {
          key: filterKey,
          label: label || '',
          value: valueId,
        }
      }),
    )
  }, [] as Tag[])
})

const unselectOption = (fieldKey: FilterGroup['key'], optionValue: Option['value']) => {
  selectedData.value[fieldKey] = selectedData.value[fieldKey].filter((v) => v !== optionValue)
}

/** Used to generate a CSS custom prop. for the map height calc() */
const { headerHeight } = useHeaderHeight()

/** Modals */
const modals = reactive({
  /** Lateral filtering panel */
  filters: {
    isOpen: false,
  },
})

const closeFiltersForm = () => {
  modals.filters.isOpen = false
  for (const key in props.filtersData) {
    if (`${props.filtersData[key]}` != '') {
      eventData('filter_usage', {
        event_source: 'gaEventDelphiDirect',
        filter_field_key: key == 'functionRoomSetupsValues' ? 'function_room_setups_values' : key,
        filter_field_value: `${props.filtersData[key]}`,
      })
    }
  }
}
</script>

<style>
@import '@/assets/styles/commons/layouts/_funnel.css';
@import '@/assets/styles/components/app-containers/_hotel-search.css';
/* Filter modal animation */
@import '@/assets/styles/animations/_transition-slide-in-right.css';
</style>
